import { useState, useMemo, useRef } from 'react'
import { CalendarIcon, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react'
import { format, parse, addMonths, subMonths } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { DateRange } from 'react-day-picker'
import { useExchangeToday } from '@/hooks/use-now'
import { cn } from '@/lib/utils'
import { buildMonths } from './calendar-months'
import { VirtualizedCalendar } from './virtualized-calendar'
import { MonthYearPicker } from './month-year-picker'

const DEFAULT_MIN_DATE = '2010-01-01'

interface SingleDateInputProps {
  /** Selected date as a `YYYY-MM-DD` string. */
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  className?: string
  /**
   * Replaces the stock "Select date" button as the popover trigger, so a
   * caller can make its own text (a grid cell, say) open the calendar in one
   * click instead of wrapping this component in a second popover. Rendered
   * through `asChild`, so it must be a single focusable element. `className`
   * and `disabled` apply to the stock button only.
   */
  trigger?: React.ReactNode
  /** Earliest pickable day, `YYYY-MM-DD`. Defaults to 2010-01-01. */
  minDate?: string
  /** Latest pickable day, `YYYY-MM-DD`. Defaults to the exchange's today. */
  maxDate?: string
}

/** Convert a Date to YYYY-MM-DD string. */
function toDateStr(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

/** Parse a YYYY-MM-DD string to a Date. Returns undefined if invalid. */
function parseDateStr(s: unknown): Date | undefined {
  if (typeof s !== 'string' || !s) return undefined
  try {
    const d = parse(s, 'yyyy-MM-dd', new Date())
    return isNaN(d.getTime()) ? undefined : d
  } catch {
    return undefined
  }
}

/** Try parsing user text input in multiple formats. */
function parseUserDate(text: string): Date | undefined {
  for (const fmt of ['M/d/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd']) {
    try {
      const d = parse(text.trim(), fmt, new Date())
      if (!isNaN(d.getTime())) return d
    } catch {
      // try next
    }
  }
  return undefined
}

/**
 * Single-date picker matching the dashboard scanner's calendar UX — the same
 * virtualized scrollable month list (`VirtualizedCalendar`) and month/year
 * navigation (`MonthYearPicker`), but committing one date instead of a range.
 * Clicking a day applies it and closes the popover (no Apply step).
 */
export function SingleDateInput({
  value,
  onChange,
  disabled,
  className,
  trigger,
  minDate,
  maxDate,
}: SingleDateInputProps) {
  const [open, setOpen] = useState(false)
  const exchangeToday = useExchangeToday()
  const today = useMemo(() => new Date(exchangeToday + 'T12:00:00'), [exchangeToday])

  const selected = useMemo(() => parseDateStr(value), [value])

  const minStr = minDate ?? DEFAULT_MIN_DATE
  const maxStr = maxDate ?? exchangeToday
  const min = useMemo(() => new Date(minStr + 'T00:00:00'), [minStr])
  const max = useMemo(() => new Date(maxStr + 'T12:00:00'), [maxStr])
  const months = buildMonths(minStr, maxStr)
  const disabledDays = [{ before: min }, { after: max }]
  const minMonth = useMemo(() => new Date(min.getFullYear(), min.getMonth(), 1), [min])
  // A future-only picker opens on its first allowed day, not a disabled today.
  const defaultStart = today >= min && today <= max ? today : min

  const [navMonth, setNavMonth] = useState<Date>(selected ?? defaultStart)
  const [showMonthPicker, setShowMonthPicker] = useState(false)
  const [text, setText] = useState('')
  const scrollToMonthRef = useRef<Date | null>(null)

  const displayLabel = selected ? format(selected, 'MMM d, yyyy') : 'Select date'

  // Highlight the single selected day by collapsing the range to {from:d, to:d}.
  const draftRange: DateRange | undefined = selected ? { from: selected, to: selected } : undefined

  const handleOpenChange = (next: boolean) => {
    if (next) {
      const start = selected ?? defaultStart
      setNavMonth(start)
      scrollToMonthRef.current = start
      setText(selected ? format(selected, 'M/d/yyyy') : '')
      setShowMonthPicker(false)
    }
    setOpen(next)
  }

  // Single click commits and closes.
  const handleDayClick = (day: Date) => {
    onChange(toDateStr(day))
    setOpen(false)
  }

  const handleTextCommit = () => {
    const d = parseUserDate(text)
    if (d && d >= min && d <= max) {
      onChange(toDateStr(d))
      setOpen(false)
    } else {
      setText(selected ? format(selected, 'M/d/yyyy') : '')
    }
  }

  const handlePrevMonth = () => {
    const prev = subMonths(navMonth, 1)
    if (prev >= minMonth) {
      setNavMonth(prev)
      scrollToMonthRef.current = prev
    }
  }

  const handleNextMonth = () => {
    const next = addMonths(navMonth, 1)
    if (next <= max) {
      setNavMonth(next)
      scrollToMonthRef.current = next
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            // `nodrag`/`nopan` stop react-flow from panning/dragging the node when
            // the picker is used inside a canvas node.
            className={cn(
              // [&_svg]:size-3 overrides shadcn Button's base [&_svg]:size-4
              // (button-variants.ts:4). Same-specificity descendant selector,
              // so the later-declared class wins via Tailwind layer order.
              'nodrag nopan h-6 w-28 shrink-0 justify-start gap-1 text-xs font-normal [&_svg]:size-3',
              className,
            )}
          >
            <CalendarIcon className="shrink-0 text-muted-foreground" />
            <span className="truncate">{displayLabel}</span>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="nodrag nopan w-auto p-0"
        align="start"
        sideOffset={6}
        onMouseDown={(e) => e.stopPropagation()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        style={{ zIndex: 60, borderColor: 'hsl(var(--border-strong))' }}
      >
        {/* Width tracks the calendar's fixed natural size: 7 day cells × 32px
            (.rdp-edge-day) = 224px grid + the .rdp-edge 4px padding, plus a
            little slack. A wider popover (was 300px) left the left-packed grid
            with dead space on the right and nav arrows wider than the grid. */}
        <div className="flex flex-col" style={{ width: 240 }}>
          {/* Text input */}
          <div className="px-3 pt-3 pb-1">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onBlur={handleTextCommit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTextCommit()
              }}
              placeholder="M/D/YYYY"
              // border-border-strong upgrades from the default --input border
              // so the text-input row reads clearly as a typeable field on
              // the popover's slightly-lighter bg-popover surface.
              className="h-7 text-xs border-border-strong"
            />
          </div>

          {/* Nav bar */}
          <div className="flex items-center justify-between px-3 py-1.5">
            <button
              onClick={handlePrevMonth}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-hover hover:text-foreground"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setShowMonthPicker((v) => !v)}
              className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium hover:bg-hover"
            >
              {format(navMonth, 'MMM yyyy').toUpperCase()}
              <ChevronDown className="h-3 w-3" />
            </button>
            <button
              onClick={handleNextMonth}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-hover hover:text-foreground"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Calendar (+ month/year overlay) */}
          <div
            className="relative border-t"
            style={{ height: 340, borderColor: 'hsl(var(--border-strong))' }}
          >
            <VirtualizedCalendar
              draftRange={draftRange}
              onRangeSelect={() => {}}
              onDayClick={handleDayClick}
              today={today}
              months={months}
              disabled={disabledDays}
              scrollToMonthRef={scrollToMonthRef}
              onVisibleMonthChange={setNavMonth}
            />
            {showMonthPicker && (
              <MonthYearPicker
                startYear={min.getFullYear()}
                endMonth={max}
                currentMonth={navMonth}
                onSelect={(month) => {
                  setNavMonth(month)
                  scrollToMonthRef.current = month
                  setShowMonthPicker(false)
                }}
                onClose={() => setShowMonthPicker(false)}
              />
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
